import { useCallback } from "react";
import { GoogleMap } from "types/map";

type MapPoint = {
  lat: number;
  lng: number;
};

type UseForecoIntroAnimationProps = {
  startCenter: MapPoint;
  endCenter: MapPoint;
  endZoom: number;
  onMapInitialized: (map: GoogleMap) => void;
  onZoomChange: (zoom: number) => void;
  onIntroComplete: () => void;
};

const animatedMaps = new WeakSet<GoogleMap>();

const START_ZOOM = 3;
const START_TILT = 0;
const END_TILT = 45;
const START_HEADING = 0;
const END_HEADING = 360;
const INTRO_DURATION_MS = 7000;
const INTRO_START_DELAY_MS = 800;
const ZOOM_UPDATE_INTERVAL_MS = 100;

const easeOutQuart = (progress: number) => 1 - Math.pow(1 - progress, 4);

const interpolate = (start: number, end: number, progress: number) =>
  start + (end - start) * progress;

export const useForecoIntroAnimation = ({
  startCenter,
  endCenter,
  endZoom,
  onMapInitialized,
  onZoomChange,
  onIntroComplete,
}: UseForecoIntroAnimationProps) => {
  const runIntroAnimation = useCallback(
    (map: GoogleMap) => {
      if (animatedMaps.has(map)) {
        return;
      }

      animatedMaps.add(map);

      map.setOptions({
        zoom: START_ZOOM,
        center: startCenter,
        tilt: START_TILT,
        heading: START_HEADING,
      });

      let startTime: number | null = null;
      let lastZoomUpdate = 0;

      const animate = (timestamp: number) => {
        if (!startTime) {
          startTime = timestamp + INTRO_START_DELAY_MS;
        }

        const elapsed = Math.max(timestamp - startTime, 0);
        const progress = Math.min(elapsed / INTRO_DURATION_MS, 1);
        const easedProgress = easeOutQuart(progress);
        const currentZoom = interpolate(START_ZOOM, endZoom, easedProgress);

        map.moveCamera({
          zoom: currentZoom,
          tilt: interpolate(START_TILT, END_TILT, easedProgress),
          heading: interpolate(START_HEADING, END_HEADING, easedProgress),
          center: {
            lat: interpolate(startCenter.lat, endCenter.lat, easedProgress),
            lng: interpolate(startCenter.lng, endCenter.lng, easedProgress),
          },
        });

        if (timestamp - lastZoomUpdate > ZOOM_UPDATE_INTERVAL_MS) {
          onZoomChange(currentZoom);
          lastZoomUpdate = timestamp;
        }

        if (progress < 1) {
          requestAnimationFrame(animate);
          return;
        }

        onZoomChange(currentZoom);
        onIntroComplete();
      };

      requestAnimationFrame(animate);
    },
    [endCenter, endZoom, onIntroComplete, onZoomChange, startCenter],
  );

  return useCallback(
    (map: GoogleMap) => {
      onMapInitialized(map);
      runIntroAnimation(map);
    },
    [onMapInitialized, runIntroAnimation],
  );
};
